import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Estimation = () => {
  const [formData, setFormData] = useState({
    serviceType: '',
    propertyType: 'residential',
    rooms: 1,
    area: 0,
    stains: 'none',
    urgency: 'standard',
    frequency: 'one-time',
    location: '',
    email: '',
    phone: '',
    // New fields for specific services
    livingRooms: 0,
    extraArea: 0,
    couchSeats: 0,
    kingMattress: 0,
    queenMattress: 0,
    singleMattress: 0,
    doubleSide: false,
    tilesArea: 0
  });

  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [breakdown, setBreakdown] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);

  const serviceTypes = [
    { 
      id: 'carpet', 
      name: 'Carpet Cleaning', 
      description: 'Professional carpet cleaning for rooms and living areas',
      minPrice: 120
    },
    { 
      id: 'couch', 
      name: 'Couch Cleaning', 
      description: 'Deep cleaning for couches and upholstery',
      minPrice: 150
    },
    { 
      id: 'tiles', 
      name: 'Tiles & Grout Cleaning', 
      description: 'Specialized cleaning for tiles and grout lines',
      minPrice: 200
    },
    { 
      id: 'mattress', 
      name: 'Mattress Cleaning', 
      description: 'Sanitization and deep cleaning for mattresses',
      minPrice: 150
    }
  ];

  const propertyTypes = [
    { id: 'residential', name: 'Residential', multiplier: 1.0 },
    { id: 'apartment', name: 'Apartment', multiplier: 1.0 },
    { id: 'office', name: 'Office', multiplier: 1.1 },
    { id: 'commercial', name: 'Commercial', multiplier: 1.2 }
  ];

  const stainLevels = [
    { id: 'none', name: 'No Stains', multiplier: 1.0 },
    { id: 'light', name: 'Light Stains', multiplier: 1.1 },
    { id: 'moderate', name: 'Moderate Stains', multiplier: 1.25 },
    { id: 'heavy', name: 'Heavy Stains', multiplier: 1.5 }
  ];

  const urgencyOptions = [
    { id: 'standard', name: 'Standard (3-5 days)', multiplier: 1.0 },
    { id: 'priority', name: 'Priority (1-2 days)', multiplier: 1.15 },
    { id: 'emergency', name: 'Emergency (Same day)', multiplier: 1.3 }
  ];

  const frequencyOptions = [
    { id: 'one-time', name: 'One-time Service', multiplier: 1.0 },
    { id: 'monthly', name: 'Monthly', multiplier: 0.95 },
    { id: 'quarterly', name: 'Quarterly', multiplier: 0.9 },
    { id: 'biannual', name: 'Biannual', multiplier: 0.85 }
  ];

  useEffect(() => {
    calculateEstimate();
  }, [formData]);

  const calculateCarpetPrice = () => {
    const { rooms, livingRooms, extraArea } = formData;
    
    const roomCost = rooms * 50; // $50 per room (up to 14 sqm)
    const livingRoomCost = livingRooms * 75; // $75 per living room (up to 20 sqm)
    const extraAreaCost = extraArea * 4; // $4 per extra sqm
    
    let subtotal = roomCost + livingRoomCost + extraAreaCost;
    subtotal = Math.max(subtotal, 120); // Minimum charge
    
    return {
      roomCost,
      livingRoomCost,
      extraAreaCost,
      subtotal
    };
  };

  const calculateCouchPrice = () => {
    const { couchSeats } = formData;
    
    const seatCost = couchSeats * 50; // $50 per seat
    let subtotal = Math.max(seatCost, 150); // Minimum charge
    
    return {
      seatCost,
      subtotal
    };
  };

  const calculateTilesPrice = () => {
    const { tilesArea } = formData;
    
    let areaCost = tilesArea * 8; // $8 per sqm
    if (tilesArea > 0 && tilesArea < 20) {
      areaCost = 200; // Minimum charge for less than 20 sqm
    }
    
    return {
      areaCost,
      subtotal: areaCost
    };
  };

  const calculateMattressPrice = () => {
    const { kingMattress, queenMattress, singleMattress, doubleSide } = formData;
    
    const kingCost = kingMattress * 100; // $100 per king mattress
    const queenCost = queenMattress * 90; // $90 per queen mattress
    const singleCost = singleMattress * 70; // $70 per single mattress
    const doubleSideCost = doubleSide ? 50 : 0; // $50 extra for double side
    
    let subtotal = kingCost + queenCost + singleCost + doubleSideCost;
    subtotal = Math.max(subtotal, 150); // Minimum charge
    
    return {
      kingCost,
      queenCost,
      singleCost,
      doubleSideCost,
      subtotal
    };
  };

  const calculateEstimate = () => {
    if (!formData.serviceType) {
      setEstimatedPrice(0);
      setGstAmount(0);
      setFinalPrice(0);
      setBreakdown({});
      return;
    }

    setIsCalculating(true);

    // Simulate calculation delay
    setTimeout(() => {
      const service = serviceTypes.find(s => s.id === formData.serviceType);
      const property = propertyTypes.find(p => p.id === formData.propertyType);
      const stain = stainLevels.find(s => s.id === formData.stains);
      const urgency = urgencyOptions.find(u => u.id === formData.urgency);
      const frequency = frequencyOptions.find(f => f.id === formData.frequency);

      let serviceCalculation = {};
      let subtotal = 0;

      // Calculate based on service type
      switch(formData.serviceType) {
        case 'carpet':
          serviceCalculation = calculateCarpetPrice();
          subtotal = serviceCalculation.subtotal;
          break;
        case 'couch':
          serviceCalculation = calculateCouchPrice();
          subtotal = serviceCalculation.subtotal;
          break;
        case 'tiles':
          serviceCalculation = calculateTilesPrice();
          subtotal = serviceCalculation.subtotal;
          break;
        case 'mattress':
          serviceCalculation = calculateMattressPrice();
          subtotal = serviceCalculation.subtotal;
          break;
        default:
          subtotal = 0;
      }

      // Apply multipliers
      const propertyMultiplier = property.multiplier;
      const stainMultiplier = stain.multiplier;
      const urgencyMultiplier = urgency.multiplier;
      const frequencyMultiplier = frequency.multiplier;

      const adjustedCost = subtotal * propertyMultiplier * stainMultiplier * urgencyMultiplier * frequencyMultiplier;
      const finalSubtotal = Math.max(service.minPrice, Math.round(adjustedCost));
      const gst = finalSubtotal * 0.10; // 10% GST
      const total = finalSubtotal + gst;

      setBreakdown({
        ...serviceCalculation,
        propertyMultiplier,
        stainMultiplier,
        urgencyMultiplier,
        frequencyMultiplier,
        subtotal: finalSubtotal,
        gst,
        total
      });

      setEstimatedPrice(finalSubtotal);
      setGstAmount(gst);
      setFinalPrice(total);
      setIsCalculating(false);
    }, 500);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert(`Thank you! Your estimate has been calculated. We'll contact you at ${formData.email} to confirm.`);
  };

  const getSavings = () => {
    const frequency = frequencyOptions.find(f => f.id === formData.frequency);
    const savings = estimatedPrice * (1 - frequency.multiplier);
    
    return Math.round(Math.max(0, savings));
  };

  const renderServiceSpecificFields = () => {
    switch(formData.serviceType) {
      case 'carpet':
        return (
          <div className="service-specific-fields">
            <div className="input-grid">
              <div className="form-group">
                <label>Number of Rooms ($50 each)</label>
                <div className="number-input">
                  <button
                    type="button"
                    onClick={() => handleInputChange('rooms', Math.max(0, formData.rooms - 1))}
                  >-</button>
                  <span>{formData.rooms}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('rooms', formData.rooms + 1)}
                  >+</button>
                </div>
              </div>
              <div className="form-group">
                <label>Living Rooms ($75 each)</label>
                <div className="number-input">
                  <button
                    type="button"
                    onClick={() => handleInputChange('livingRooms', Math.max(0, formData.livingRooms - 1))}
                  >-</button>
                  <span>{formData.livingRooms}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('livingRooms', formData.livingRooms + 1)}
                  >+</button>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>
                Extra Area ($4 per sqm)
                <span className="area-value">{formData.extraArea} sqm</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={formData.extraArea}
                onChange={(e) => handleInputChange('extraArea', parseInt(e.target.value))}
                className="area-slider"
              />
              <div className="slider-labels">
                <span>0 sqm</span>
                <span>100 sqm</span>
              </div>
            </div>
          </div>
        );
      
      case 'couch':
        return (
          <div className="service-specific-fields">
            <div className="form-group">
              <label>Number of Seats ($50 per seat)</label>
              <div className="number-input">
                <button
                  type="button"
                  onClick={() => handleInputChange('couchSeats', Math.max(0, formData.couchSeats - 1))}
                >-</button>
                <span>{formData.couchSeats}</span>
                <button
                  type="button"
                  onClick={() => handleInputChange('couchSeats', formData.couchSeats + 1)}
                >+</button>
              </div>
            </div>
          </div>
        );
      
      case 'tiles':
        return (
          <div className="service-specific-fields">
            <div className="form-group">
              <label>
                Area to Clean ($8 per sqm)
                <span className="area-value">{formData.tilesArea} sqm</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={formData.tilesArea}
                onChange={(e) => handleInputChange('tilesArea', parseInt(e.target.value))}
                className="area-slider"
              />
              <div className="slider-labels">
                <span>0 sqm</span>
                <span>100 sqm</span>
              </div>
              <div className="info-text">Minimum 20 sqm for $200</div>
            </div>
          </div>
        );
      
      case 'mattress':
        return (
          <div className="service-specific-fields">
            <div className="input-grid">
              <div className="form-group">
                <label>King Mattress ($100)</label>
                <div className="number-input">
                  <button
                    type="button"
                    onClick={() => handleInputChange('kingMattress', Math.max(0, formData.kingMattress - 1))}
                  >-</button>
                  <span>{formData.kingMattress}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('kingMattress', formData.kingMattress + 1)}
                  >+</button>
                </div>
              </div>
              <div className="form-group">
                <label>Queen Mattress ($90)</label>
                <div className="number-input">
                  <button
                    type="button"
                    onClick={() => handleInputChange('queenMattress', Math.max(0, formData.queenMattress - 1))}
                  >-</button>
                  <span>{formData.queenMattress}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('queenMattress', formData.queenMattress + 1)}
                  >+</button>
                </div>
              </div>
              <div className="form-group">
                <label>Single Mattress ($70)</label>
                <div className="number-input">
                  <button
                    type="button"
                    onClick={() => handleInputChange('singleMattress', Math.max(0, formData.singleMattress - 1))}
                  >-</button>
                  <span>{formData.singleMattress}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('singleMattress', formData.singleMattress + 1)}
                  >+</button>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.doubleSide}
                  onChange={(e) => handleInputChange('doubleSide', e.target.checked)}
                />
                Double Side Cleaning (+$50)
              </label>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderBreakdown = () => {
    if (!formData.serviceType || !breakdown.subtotal) return null;

    switch(formData.serviceType) {
      case 'carpet':
        return (
          <div className="breakdown-list">
            {breakdown.roomCost > 0 && (
              <div className="breakdown-item">
                <span>Rooms ({formData.rooms} x $50)</span>
                <span>${breakdown.roomCost}</span>
              </div>
            )}
            {breakdown.livingRoomCost > 0 && (
              <div className="breakdown-item">
                <span>Living Rooms ({formData.livingRooms} x $75)</span>
                <span>${breakdown.livingRoomCost}</span>
              </div>
            )}
            {breakdown.extraAreaCost > 0 && (
              <div className="breakdown-item">
                <span>Extra Area ({formData.extraArea} sqm x $4)</span>
                <span>${breakdown.extraAreaCost}</span>
              </div>
            )}
            {breakdown.subtotal === 120 && (
              <div className="breakdown-item">
                <span>Minimum Charge Applied</span>
                <span>$120</span>
              </div>
            )}
          </div>
        );
      
      case 'couch':
        return (
          <div className="breakdown-list">
            {breakdown.seatCost > 0 && (
              <div className="breakdown-item">
                <span>Seats ({formData.couchSeats} x $50)</span>
                <span>${breakdown.seatCost}</span>
              </div>
            )}
            {breakdown.subtotal === 150 && (
              <div className="breakdown-item">
                <span>Minimum Charge Applied</span>
                <span>$150</span>
              </div>
            )}
          </div>
        );
      
      case 'tiles':
        return (
          <div className="breakdown-list">
            {formData.tilesArea >= 20 ? (
              <div className="breakdown-item">
                <span>Area ({formData.tilesArea} sqm x $8)</span>
                <span>${breakdown.areaCost}</span>
              </div>
            ) : (
              <div className="breakdown-item">
                <span>Minimum Charge (under 20 sqm)</span>
                <span>$200</span>
              </div>
            )}
          </div>
        );
      
      case 'mattress':
        return (
          <div className="breakdown-list">
            {breakdown.kingCost > 0 && (
              <div className="breakdown-item">
                <span>King Mattress ({formData.kingMattress} x $100)</span>
                <span>${breakdown.kingCost}</span>
              </div>
            )}
            {breakdown.queenCost > 0 && (
              <div className="breakdown-item">
                <span>Queen Mattress ({formData.queenMattress} x $90)</span>
                <span>${breakdown.queenCost}</span>
              </div>
            )}
            {breakdown.singleCost > 0 && (
              <div className="breakdown-item">
                <span>Single Mattress ({formData.singleMattress} x $70)</span>
                <span>${breakdown.singleCost}</span>
              </div>
            )}
            {breakdown.doubleSideCost > 0 && (
              <div className="breakdown-item">
                <span>Double Side Cleaning</span>
                <span>${breakdown.doubleSideCost}</span>
              </div>
            )}
            {breakdown.subtotal === 150 && (
              <div className="breakdown-item">
                <span>Minimum Charge Applied</span>
                <span>$150</span>
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <SEO 
        title="Free Carpet Cleaning Estimate - Instant Quote"
        description="Get a free, instant estimate for your carpet cleaning project. No obligation. Calculate pricing based on room size, stains, and service type. Transparent pricing guaranteed."
        keywords="carpet cleaning estimate, free quote, pricing calculator, cost estimate, instant quote"
        url="https://greenrockycarpet.com.au/estimation"
        section="Estimation"
      />

    <div className="estimation-page">
      {/* Hero Section */}
      <section className="estimation-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Get Your Instant Estimate</h1>
            <p className="hero-subtitle">
              Receive an accurate quote in seconds. No hidden fees, no surprises.
            </p>
          </div>
        </div>
      </section>

      <div className="estimation-container">
        <div className="container">
          <div className="estimation-grid">
            {/* Estimation Form */}
            <div className="estimation-form-section">
              <div className="form-header">
                <h2>Tell Us About Your Needs</h2>
                <p>Fill out the form below to get your instant estimate</p>
              </div>

              <form onSubmit={handleSubmit} className="estimation-form">
                {/* Service Type */}
                <div className="form-section">
                  <label className="section-label">Service Type</label>
                  <div className="est-service-cards">
                    {serviceTypes.map(service => (
                      <div
                        key={service.id}
                        className={`est-service-card ${formData.serviceType === service.id ? 'selected' : ''}`}
                        onClick={() => handleInputChange('serviceType', service.id)}
                      >
                        <div className="est-service-icon">
                          {service.id === 'carpet' && '🧹'}
                          {service.id === 'couch' && '🛋️'}
                          {service.id === 'tiles' && '🧱'}
                          {service.id === 'mattress' && '🛏️'}
                        </div>
                        <div className="est-service-info">
                          <h4>{service.name}</h4>
                          <p>{service.description}</p>
                          <div className="est-service-min">Min: ${service.minPrice}</div>
                        </div>
                        <div className="est-selection-indicator"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Service Specific Fields */}
                {formData.serviceType && (
                  <div className="form-section">
                    <label className="section-label">Service Details</label>
                    {renderServiceSpecificFields()}
                  </div>
                )}

                {/* Property Details */}
                <div className="form-section">
                  <label className="section-label">Property Details</label>
                  <div className="form-group">
                    <label>Property Type</label>
                    <select
                      value={formData.propertyType}
                      onChange={(e) => handleInputChange('propertyType', e.target.value)}
                    >
                      {propertyTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Additional Options */}
                <div className="form-section">
                  <label className="section-label">Additional Options</label>
                  <div className="options-grid">
                    <div className="form-group">
                      <label>Stain Level</label>
                      <select
                        value={formData.stains}
                        onChange={(e) => handleInputChange('stains', e.target.value)}
                      >
                        {stainLevels.map(level => (
                          <option key={level.id} value={level.id}>{level.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Service Urgency</label>
                      <select
                        value={formData.urgency}
                        onChange={(e) => handleInputChange('urgency', e.target.value)}
                      >
                        {urgencyOptions.map(option => (
                          <option key={option.id} value={option.id}>{option.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Service Frequency</label>
                      <select
                        value={formData.frequency}
                        onChange={(e) => handleInputChange('frequency', e.target.value)}
                      >
                        {frequencyOptions.map(option => (
                          <option key={option.id} value={option.id}>{option.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="form-section">
                  <label className="section-label">Contact Information</label>
                  <div className="input-grid">
                    <div className="form-group">
                      <label>Email Address</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Location (ZIP Code)</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Enter your ZIP code"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Estimate Results */}
            <div className="estimate-results-section">
              <div className="estimate-card">
                <div className="estimate-header">
                  <h3>Your Estimate</h3>
                  <div className="price-badge">
                    {isCalculating ? (
                      <div className="calculating">Calculating...</div>
                    ) : (
                      <>
                        <div className="price">${finalPrice.toFixed(2)}</div>
                        <div className="price-label">Total Including GST</div>
                      </>
                    )}
                  </div>
                </div>

                {estimatedPrice > 0 && !isCalculating && (
                  <>
                    {/* Cost Breakdown */}
                    <div className="breakdown-section">
                      <h4>Cost Breakdown</h4>
                      {renderBreakdown()}
                      
                      {/* Multipliers */}
                      {(breakdown.propertyMultiplier !== 1 || breakdown.stainMultiplier !== 1 || 
                        breakdown.urgencyMultiplier !== 1 || breakdown.frequencyMultiplier !== 1) && (
                        <div className="multipliers-section">
                          <div className="breakdown-divider"></div>
                          {breakdown.propertyMultiplier !== 1 && (
                            <div className="breakdown-item">
                              <span>Property Type ({formData.propertyType})</span>
                              <span>x{breakdown.propertyMultiplier}</span>
                            </div>
                          )}
                          {breakdown.stainMultiplier !== 1 && (
                            <div className="breakdown-item">
                              <span>Stain Level ({formData.stains})</span>
                              <span>x{breakdown.stainMultiplier}</span>
                            </div>
                          )}
                          {breakdown.urgencyMultiplier !== 1 && (
                            <div className="breakdown-item">
                              <span>Service Urgency</span>
                              <span>x{breakdown.urgencyMultiplier}</span>
                            </div>
                          )}
                          {breakdown.frequencyMultiplier !== 1 && (
                            <div className="breakdown-item">
                              <span>Service Frequency</span>
                              <span>x{breakdown.frequencyMultiplier}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="breakdown-divider"></div>
                      <div className="breakdown-item">
                        <span>Subtotal</span>
                        <span>${breakdown.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>GST (10%)</span>
                        <span>${breakdown.gst.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item total">
                        <span>Total Amount</span>
                        <span>${breakdown.total.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Savings Display */}
                    {getSavings() > 0 && (
                      <div className="savings-banner">
                        <div className="savings-icon">💰</div>
                        <div className="savings-content">
                          <div className="savings-title">You're Saving ${getSavings()}!</div>
                          <div className="savings-subtitle">
                            {formData.frequency === 'monthly' && 'Monthly subscription discount applied'}
                            {formData.frequency === 'quarterly' && 'Quarterly service discount applied'}
                            {formData.frequency === 'biannual' && 'Biannual service discount applied'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Features Included */}
                    <div className="features-section">
                      <h4>What's Included</h4>
                      <div className="features-list">
                        <div className="feature-item">
                          <span className="check-icon">✓</span>
                          Eco-friendly cleaning products
                        </div>
                        <div className="feature-item">
                          <span className="check-icon">✓</span>
                          Professional equipment
                        </div>
                        <div className="feature-item">
                          <span className="check-icon">✓</span>
                          Satisfaction guarantee
                        </div>
                        <div className="feature-item">
                          <span className="check-icon">✓</span>
                          Certified technicians
                        </div>
                        <div className="feature-item">
                          <span className="check-icon">✓</span>
                          Insurance covered
                        </div>
                      </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="estimate-actions">
                      <button 
                        type="submit" 
                        className="btn-primary"
                        onClick={handleSubmit}
                      >
                        Book This Service
                      </button>
                      <Link to="/contact" className="btn-secondary">
                        Contact for Custom Quote
                      </Link>
                    </div>
                  </>
                )}

                {!formData.serviceType && !isCalculating && (
                  <div className="empty-state">
                    <div className="empty-icon">📊</div>
                    <h4>Select a Service</h4>
                    <p>Choose a service type to see your instant estimate</p>
                  </div>
                )}
              </div>

              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="trust-badge">
                  <div className="badge-icon">🔒</div>
                  <span>Price Lock Guarantee</span>
                </div>
                <div className="trust-badge">
                  <div className="badge-icon">⭐</div>
                  <span>No Hidden Fees</span>
                </div>
                <div className="trust-badge">
                  <div className="badge-icon">🔄</div>
                  <span>Free Re-clean if Needed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="estimation-faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How accurate is this estimate?</h4>
              <p>Our estimates are based on standard pricing. Final pricing may vary based on specific conditions assessed during the service.</p>
            </div>
            <div className="faq-item">
              <h4>Is GST included in the price?</h4>
              <p>Yes, all prices include 10% GST as required by law. The breakdown shows the subtotal and GST separately.</p>
            </div>
            <div className="faq-item">
              <h4>Do you offer package deals?</h4>
              <p>Yes! We offer discounts for multiple services or recurring appointments. Contact us for custom package pricing.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
};

export default Estimation;